/**
 * Color System - Central Export Point
 *
 * This is the single entry point for all color-related imports.
 */

import {colorRegistry} from './registry'

export {getColor, getColorByAlias, getHexCode, requireHexCode} from './registry'

/** @public - Used by .mac.ts files (which are in knip ignore list) */
export {brandColorsMac} from './registry'

/**
 * Generate background theme class string for a color
 */
function generateThemeClass(
	colorId: string,
	textContrast: 'light' | 'dark'
): string {
	const bgClass = `bg-${colorId}`
	if (textContrast === 'dark') {
		return `${bgClass} text-black fill-black text-black-links`
	}
	return `${bgClass} text-white fill-white`
}

/**
 * Build background themes map from registry
 */
function buildBackgroundThemes(): Map<string, string> {
	const themes = new Map<string, string>()

	// Special non-registry themes (core design system colors)
	themes.set('none', '')
	themes.set('black', 'bg-black text-white fill-white')
	themes.set('white', 'bg-white text-black fill-black text-black-links')
	themes.set('midnight_blue', 'bg-midnight-blue text-white fill-white')
	themes.set('elanco_blue', 'bg-elanco-blue text-white fill-white')
	themes.set(
		'elanco_beige',
		'bg-elanco-beige text-black fill-black text-black-links'
	)
	themes.set('pet_green', 'bg-pet-green text-white fill-white')
	themes.set('vet_purple', 'bg-vet text-white fill-white')
	themes.set(
		'elanco_green',
		'bg-elanco-green text-black fill-black text-black-links'
	)
	themes.set('elanco_grey', 'bg-elanco-grey text-white fill-white')
	themes.set(
		'light_blue',
		'bg-light-blue text-black fill-black text-black-links'
	)
	themes.set(
		'light_gray',
		'bg-light_gray text-black fill-black text-black-links'
	)
	themes.set(
		'mid_beige',
		'bg-mid-beige text-black fill-black text-black-links'
	)
	themes.set(
		'pet_green_light',
		'bg-pet-green-light text-black fill-black text-black-links'
	)
	themes.set(
		'purple_light',
		'bg-purple-light text-black fill-black text-black-links'
	)

	// Generate from registry - textContrast defaults to 'light' (white text)
	// Only add if not already in special themes (preserves special bg class names)
	for (const [id, color] of Object.entries(colorRegistry)) {
		if (!themes.has(id)) {
			const contrast: 'light' | 'dark' = color.textContrast ?? 'light'
			themes.set(id, generateThemeClass(id, contrast))
		}
		// Also register aliases so removed duplicate entries still resolve
		for (const alias of color.aliases) {
			if (!themes.has(alias)) {
				const contrast: 'light' | 'dark' = color.textContrast ?? 'light'
				themes.set(alias, generateThemeClass(id, contrast))
			}
		}
	}

	// Register underscore/hyphen variants so both `elanco-grey` and
	// `elanco_grey` resolve to the same theme string.  The loop only adds
	// variants that don't already have a dedicated entry, so manual special
	// entries (which may use a different bg-class) are never overwritten.
	const entries = [...themes.entries()]
	for (const [key, value] of entries) {
		const alt = key.includes('-')
			? key.replace(/-/g, '_')
			: key.replace(/_/g, '-')
		if (alt !== key && !themes.has(alt)) {
			themes.set(alt, value)
		}
	}

	return themes
}

/**
 * Background themes map - auto-generated from Color Registry
 * Use this for applying background colors with proper text contrast
 */
export const backgroundThemes = buildBackgroundThemes()

/**
 * Get background theme for a color ID.
 * Handles both underscore and hyphen variants (e.g. 'elanco_grey' and 'elanco-grey').
 * @param colorId - The color ID
 * @returns Theme class string or empty string if not found
 */
export function getBackgroundTheme(colorId: string): string {
	return backgroundThemes.get(colorId) ?? ''
}

/**
 * Get only the background Tailwind class for a color ID (e.g. `bg-seresto`).
 * Use this when text/fill contrast is controlled separately (e.g. CMS hasWhiteText).
 */
export function getBackgroundClass(colorId: string): string {
	const theme = getBackgroundTheme(colorId)
	if (!theme) return ''
	return theme.split(/\s+/).find((token) => token.startsWith('bg-')) ?? ''
}
